/**
 * ChatView — Ember streaming chat
 *
 * Connects to /api/chat/* SSE endpoints (chat-routes.ts).
 * Uses fetch() + ReadableStream for POST-based SSE streaming.
 * All markdown is sanitized (HTML-escaped) before rendering.
 */

import { useState, useRef, useEffect, useCallback } from 'react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
}

const BASE = '/api/chat';

// ── Markdown renderer ─────────────────────────────────────────────────────────
// HTML is escaped first, then markdown syntax applied — safe from XSS.
// NOTE: Hex values in style= attributes are hardcoded because CSS vars
// cannot be used inside innerHTML strings.

function renderMarkdown(raw: string): string {
  const escaped = raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  return (
    escaped
      // Fenced code blocks (``` ... ```)
      .replace(
        /```[\w]*\n?([\s\S]*?)```/g,
        '<pre style="background:#0d0d1a;border:1px solid #2a2a4a;border-radius:6px;padding:12px 14px;overflow-x:auto;font-size:0.8rem;line-height:1.5;margin:10px 0;font-family:\'SF Mono\',Monaco,monospace;"><code>$1</code></pre>',
      )
      // Inline code
      .replace(
        /`([^`]+)`/g,
        '<code style="background:#1a1a2e;border:1px solid #2a2a4a;border-radius:3px;padding:2px 6px;font-size:0.82em;font-family:\'SF Mono\',Monaco,monospace;">$1</code>',
      )
      // Bold
      .replace(/\*\*([^*\n]+)\*\*/g, '<strong style="color:#c8c8ff;">$1</strong>')
      // H3
      .replace(
        /^### (.+)$/gm,
        '<h4 style="color:#8b8bff;margin:14px 0 4px;font-size:0.88rem;font-weight:700;letter-spacing:0.03em;">$1</h4>',
      )
      // H2
      .replace(
        /^## (.+)$/gm,
        '<h3 style="color:#8b8bff;margin:16px 0 6px;font-size:0.95rem;font-weight:700;letter-spacing:0.03em;">$1</h3>',
      )
      // H1
      .replace(
        /^# (.+)$/gm,
        '<h2 style="color:#a0a0ff;margin:18px 0 8px;font-size:1.05rem;font-weight:700;letter-spacing:0.03em;">$1</h2>',
      )
      // List items (- or *)
      .replace(
        /^[ \t]*[-*] (.+)$/gm,
        '<li style="margin:3px 0 3px 16px;list-style:disc;color:#c8c8e8;">$1</li>',
      )
      // Numbered list
      .replace(
        /^[ \t]*(\d+)\. (.+)$/gm,
        '<li style="margin:3px 0 3px 16px;list-style:decimal;color:#c8c8e8;">$2</li>',
      )
      // Horizontal rule
      .replace(/^---$/gm, '<hr style="border:none;border-top:1px solid #2a2a4a;margin:16px 0;">')
      // Newlines → br (but not inside pre blocks — we handle that via ordering)
      .replace(/\n/g, '<br>')
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ChatView() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const onSuggest = useCallback((text: string) => {
    setInput(text);
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;

    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setStreaming(true);

    // Add user + placeholder assistant in one state update
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: text },
      { role: 'assistant', content: '', streaming: true },
    ]);

    abortRef.current = new AbortController();

    try {
      const res = await fetch(`${BASE}/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, sessionId }),
        signal: abortRef.current.signal,
      });

      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = {
            role: 'assistant',
            content: `Error: ${err.error || res.statusText}`,
          };
          return next;
        });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data) continue;

          try {
            const parsed = JSON.parse(data);
            if (parsed.type === 'session' && parsed.sessionId) {
              setSessionId(parsed.sessionId);
            } else if (parsed.type === 'delta' && parsed.content) {
              fullContent += parsed.content;
              const captured = fullContent;
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = {
                  role: 'assistant',
                  content: captured,
                  streaming: true,
                };
                return next;
              });
            } else if (parsed.type === 'done' || parsed.type === 'error') {
              const finalContent = parsed.type === 'error' ? `Error: ${parsed.error}` : fullContent;
              setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { role: 'assistant', content: finalContent };
                return next;
              });
            }
          } catch {
            // Skip malformed SSE chunks
          }
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        // Finalize whatever we streamed
        setMessages((prev) => {
          const next = [...prev];
          if (next[next.length - 1]?.streaming) {
            next[next.length - 1] = { ...next[next.length - 1], streaming: false };
          }
          return next;
        });
      } else {
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = {
            role: 'assistant',
            content: `Error: ${err.message || String(err)}`,
          };
          return next;
        });
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [input, sessionId, streaming]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const clear = useCallback(() => {
    if (sessionId) {
      fetch(`${BASE}/sessions/${sessionId}`, { method: 'DELETE' }).catch(() => {});
    }
    setMessages([]);
    setSessionId(null);
  }, [sessionId]);

  const handleKey = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        send();
      }
    },
    [send],
  );

  return (
    <div className="amp-chat-wrap">
      {/* Header */}
      <div className="amp-chat-header">
        <div className="amp-chat-header-left">
          <span style={{ fontSize: '1rem' }}>🔥</span>
          <span className="amp-brand-text">Ember</span>
          <span className="amp-divider">|</span>
          <span className="amp-subtitle">Chief of Staff</span>
          {sessionId && (
            <>
              <span className="amp-divider">|</span>
              <span className="amp-chat-session-id">{sessionId.slice(0, 8)}</span>
            </>
          )}
        </div>
        {messages.length > 0 && (
          <button className="amp-chat-clear-btn" onClick={clear} disabled={streaming}>
            New session
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="amp-chat-messages">
        {messages.length === 0 && <EmptyState onSuggest={onSuggest} />}
        {messages.map((msg, i) => (
          <MessageRow key={i} message={msg} />
        ))}
        <div ref={messagesEndRef} style={{ height: 24 }} />
      </div>

      {/* Input bar */}
      <div className="amp-chat-input-bar">
        <textarea
          ref={textareaRef}
          className="amp-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          onInput={(e) => {
            const t = e.currentTarget;
            t.style.height = 'auto';
            t.style.height = Math.min(t.scrollHeight, 150) + 'px';
          }}
          placeholder="Message Ember… (Enter to send, Shift+Enter for newline)"
          disabled={streaming}
          rows={1}
        />
        {streaming ? (
          <button className="amp-chat-stop" onClick={stop}>
            Stop
          </button>
        ) : (
          <button
            className={`amp-chat-send${input.trim() ? ' amp-chat-send--active' : ''}`}
            onClick={send}
            disabled={!input.trim()}
          >
            Send
          </button>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function EmptyState({ onSuggest }: { onSuggest: (text: string) => void }) {
  return (
    <div className="amp-chat-empty">
      <div className="amp-chat-empty-icon">🔥</div>
      <div className="amp-chat-empty-title">Ember is ready</div>
      <div className="amp-chat-empty-desc">
        Ask about OpCentral architecture, Trusted.Associates strategy, code, or anything else.
        Direct, honest, no fluff.
      </div>
      <div className="amp-chat-suggestions">
        {[
          "What's left to build in Delivr?",
          'Review amplifyr architecture',
          'Status on Flourish?',
          'What is EmpowerPax?',
        ].map((prompt) => (
          <button key={prompt} className="amp-chat-suggestion" onClick={() => onSuggest(prompt)}>
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageRow({ message }: { message: Message }) {
  const isUser = message.role === 'user';

  return (
    <div className={`amp-chat-row${isUser ? '' : ' amp-chat-row--assistant'}`}>
      <div className="amp-chat-row-inner">
        <div
          className={`amp-chat-avatar ${isUser ? 'amp-chat-avatar--user' : 'amp-chat-avatar--assistant'}`}
        >
          {isUser ? 'D' : '🔥'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className={`amp-chat-sender ${isUser ? 'amp-chat-sender--user' : 'amp-chat-sender--assistant'}`}
          >
            {isUser ? 'Daniel' : 'Ember'}
            {message.streaming && <span className="amp-chat-streaming-tag">● streaming</span>}
          </div>
          {isUser ? (
            <div className="amp-chat-user-text">{message.content}</div>
          ) : (
            <div
              className="amp-chat-assistant-text"
              dangerouslySetInnerHTML={{
                __html:
                  renderMarkdown(message.content) +
                  (message.streaming ? '<span class="ember-cursor"></span>' : ''),
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
